import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { withdrawalService } from "../../../services/cashier/withdrawal.service";
import { OrderType } from "../../../components/affiliate/Trackers";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";

export type payloadList = {
  page: number;
  perPage?: number;
  order: OrderType;
  filter: {
    search?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    transactionType?: string | null;
    wallet?: any | null;
  }
};

export type payloadWidgetList = {
  page: number;
  perPage?: number;
  order: OrderType;
};

export type listItem = {
  id: number;
  username: string;
  status: string;
  type: string;
  email: string;
  createdAt: string;
  level: number;
  allSubAgents: number;
  directSubAgents: number;
  playersCount: number;
};

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
  startRowNum: number;
  endRowNum: number;
  totalEntries: number | string;
  totalPages: number;
  totalPending?: number;
}

export type withdrawalSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  decline: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  approve: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  widget: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: withdrawalSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  decline: {
    data: null,
    error: null,
    isFetching: false,
  },
  approve: {
    data: null,
    error: null,
    isFetching: false,
  },
  widget: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'cashier/withdrawal/getListData',
  async (payload: payloadList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await withdrawalService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const setDeclineData: any = createAsyncThunk(
  'cashier/withdrawal/setDeclineData',
  async (payload: { id: string }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await withdrawalService.setDecline(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.withdrawalDeclined',
          })
        );
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const setApproveData: any = createAsyncThunk(
  'cashier/withdrawal/setApproveData',
  async (payload: { id: string }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await withdrawalService.setApprove(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.withdrawalApproved',
          })
        );
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getWidgetListData: any = createAsyncThunk(
  'cashier/withdrawal/getWidgetListData',
  async (payload: payloadWidgetList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await withdrawalService.getWidgetList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);


export const withdrawalSlice = createSlice({
  name: 'withdrawal',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearDeclineState(state) {
      state.decline.error = null;
      state.decline.isFetching = false;
      state.decline.data = null;
    },
    clearApproveState(state) {
      state.approve.error = null;
      state.approve.isFetching = false;
      state.approve.data = null;
    },
    clearWidgetListState(state) {
      state.widget.error = null;
      state.widget.isFetching = false;
      state.widget.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<withdrawalSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

    builder.addCase(setDeclineData.pending, (state ) => {
      state.decline.isFetching = true;
      state.decline.error = null;
    });
    builder.addCase(setDeclineData.fulfilled, (state , action) => {
      state.decline.error = null;
      state.decline.isFetching = false;
      state.decline.data = action.payload;
    });
    builder.addCase(setDeclineData.rejected, (state , action) => {
      state.decline.isFetching = false;
      state.decline.error = action.payload;
      state.decline.data = null;
    });

    builder.addCase(setApproveData.pending, (state ) => {
      state.approve.isFetching = true;
      state.approve.error = null;
    });
    builder.addCase(setApproveData.fulfilled, (state , action) => {
      state.approve.error = null;
      state.approve.isFetching = false;
      state.approve.data = action.payload;
    });
    builder.addCase(setApproveData.rejected, (state , action) => {
      state.approve.isFetching = false;
      state.approve.error = action.payload;
      state.approve.data = null;
    });

    builder.addCase(getWidgetListData.pending, (state ) => {
      state.widget.isFetching = true;
      state.widget.error = null;
    });
    builder.addCase(getWidgetListData.fulfilled, (state , action) => {
      state.widget.error = null;
      state.widget.isFetching = false;
      state.widget.data = action.payload;
    });
    builder.addCase(getWidgetListData.rejected, (state , action) => {
      state.widget.isFetching = false;
      state.widget.error = action.payload;
      state.widget.data = null;
    });
  },
});

export const {
  clearListState,
  clearDeclineState,
  clearApproveState,
  clearWidgetListState,
} = withdrawalSlice.actions;

export default withdrawalSlice.reducer;


